import React, { useContext, useEffect } from 'react';
import { Buffer, Card } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { useVariationPageThumbnail } from 'packages/pages/design/VariationsContext/useVariationPageThumbnail';
import { VariationsThumbnailsContext } from 'packages/pages/design/VariationsContext/VariationsThumbnailsProvider';
import { variationsThumbnailsStore } from 'packages/pages/design/VariationsContext/variationsThumbnailsStore';
import { isPageInvalid } from 'packages/pages/design/sidebar/variations/utils';
import { VariationSetItemEntryType } from 'packages/variation-export/types';

type VariationPreviewProps = {
    variation: VariationSetItemEntryType;
    onSelect: () => void;
    isSelected: boolean;
    pageIndex: number;
};

export const VariationPreview = ({ variation, onSelect, isSelected, pageIndex }: VariationPreviewProps) => {
    const { variationThumbnailRenderer } = useContext(VariationsThumbnailsContext);
    const { translate } = useTranslate();
    const { variationSetId: variationId, containsTransparency } = variation;
    const { variationPageThumbnail, loading } = useVariationPageThumbnail(
        variationId,
        pageIndex,
        variationId,
        300,
        300,
    );

    useEffect(() => {
        if (!variationsThumbnailsStore.isThumbnailsReady(variationId, pageIndex, 300, 300)) {
            variationThumbnailRenderer.render(variationId, pageIndex, 300, 300);
        }
    }, [variationId, pageIndex, variationThumbnailRenderer]);

    const hasError = isPageInvalid(variation);

    const additionalProps = !hasError
        ? {
              selectButtonAriaLabel: 'Select variation',
              onSelect,
              onClick: onSelect,
              isSelected,
              isDisabled: hasError,
          }
        : {
              selectButtonAriaLabel: 'Select variation',
              onSelect: undefined,
              onClick: undefined,
              isSelected: false,
          };

    return (
        <div id={`v-${variationId}-${pageIndex}`}>
            <Card variant="clean" {...additionalProps}>
                <Card.Frame
                    isCheckeredBackground={containsTransparency}
                    progressBar={loading && <Buffer noCornerRadius />}
                    badge={hasError && translate('modal.quick-export.variation.error')}
                >
                    {variationPageThumbnail?.dataUrl && (
                        <Card.Images>
                            <Card.Img src={variationPageThumbnail?.dataUrl} contentResize="fit" />
                        </Card.Images>
                    )}
                </Card.Frame>
            </Card>
        </div>
    );
};

export default VariationPreview;
